import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Card,
    CardHeader,
    CardContent,
    Grid,
    Typography,
    Alert,
    CircularProgress,
    Divider,
    MenuItem,
    Chip
} from '@mui/material';
import {
    Save as SaveIcon,
    Cancel as CancelIcon,
    LocalShipping as VehicleIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { vehicleService } from '../../../services/vehicleService';

const VEHICLE_TYPES = ['Bike', 'Auto', 'Van', 'Truck', 'Tempo', 'Car', 'Other'];

const VehicleForm = ({ vehicle, onSuccess, onCancel, viewMode = false }) => {
    const { showError } = useApiHandler();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        vehicle_number: '',
        vehicle_type: '',
        status: 1
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (vehicle) {
            setFormData({
                vehicle_number: vehicle.vehicle_number || '',
                vehicle_type: vehicle.vehicle_type || '',
                status: (vehicle.status === false || vehicle.status === 0) ? 0 : 1
            });
        }
    }, [vehicle]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.vehicle_number.trim()) {
            newErrors.vehicle_number = 'Vehicle number is required';
        } else if (formData.vehicle_number.length > 50) {
            newErrors.vehicle_number = 'Vehicle number must be less than 50 characters';
        }

        if (!formData.vehicle_type) {
            newErrors.vehicle_type = 'Vehicle type is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const vehicleData = {
                vehicle_number: formData.vehicle_number.trim(),
                vehicle_type: formData.vehicle_type,
                status: parseInt(formData.status)
            };

            if (vehicle) {
                await vehicleService.updateVehicle(vehicle.id, vehicleData);
            } else {
                await vehicleService.createVehicle(vehicleData);
            }

            onSuccess();
        } catch (error) {
            console.error('Error during operation:', error);
            showError('Operation failed. Please try again.');

            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(field => {
                    newErrors[field] = error.errors[field][0];
                });
                setErrors(newErrors);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader
                avatar={<VehicleIcon color="primary" />}
                title={
                    viewMode
                        ? 'Vehicle Details'
                        : vehicle
                            ? 'Edit Vehicle'
                            : 'Create New Vehicle'
                }
                subheader={
                    viewMode
                        ? 'View vehicle information'
                        : vehicle
                            ? 'Update vehicle information'
                            : 'Add a new vehicle for shipment tracking'
                }
            />
            <Divider />
            <CardContent>
                <Grid container spacing={3}>
                    {/* Vehicle Number */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Vehicle Number"
                            value={formData.vehicle_number}
                            onChange={(e) => handleInputChange('vehicle_number', e.target.value)}
                            error={!!errors.vehicle_number}
                            helperText={errors.vehicle_number || 'e.g., TN01AB1234'}
                            disabled={viewMode || loading}
                            required
                        />
                    </Grid>

                    {/* Vehicle Type */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Vehicle Type"
                            value={formData.vehicle_type}
                            onChange={(e) => handleInputChange('vehicle_type', e.target.value)}
                            error={!!errors.vehicle_type}
                            helperText={errors.vehicle_type || 'Select the type of vehicle'}
                            disabled={viewMode || loading}
                            required
                        >
                            {VEHICLE_TYPES.map((type) => (
                                <MenuItem key={type} value={type}>{type}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            select
                            fullWidth
                            label="Status"
                            value={formData.status}
                            onChange={(e) => handleInputChange('status', parseInt(e.target.value))}
                            disabled={viewMode || loading}
                            required
                        >
                            <MenuItem value={1}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="Active" color="success" size="small" />
                                    <Typography variant="body2">Available for tracking</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem value={0}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label="Inactive" color="default" size="small" />
                                    <Typography variant="body2">Hidden from tracking</Typography>
                                </Box>
                            </MenuItem>
                        </TextField>
                    </Grid>

                    {/* Created Date (View Mode) */}
                    {viewMode && vehicle && (
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Created Date"
                                value={vehicle.created_at ? new Date(vehicle.created_at).toLocaleDateString() : 'N/A'}
                                disabled
                                helperText="Date when this vehicle was created"
                            />
                        </Grid>
                    )}

                    {/* Information Alert */}
                    <Grid item xs={12}>
                        <Alert severity="info" icon={<InfoIcon />}>
                            <Typography variant="body2">
                                <strong>Vehicle Guidelines:</strong>
                                <br />
                                • Vehicle numbers must be unique
                                <br />
                                • Only active vehicles appear in the mobile tracking picker
                                <br />
                                • Agents can still enter a temporary vehicle number during tracking
                            </Typography>
                        </Alert>
                    </Grid>
                </Grid>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                        startIcon={<CancelIcon />}
                    >
                        {viewMode ? 'Close' : 'Cancel'}
                    </Button>

                    {!viewMode && (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                        >
                            {loading ? 'Saving...' : (vehicle ? 'Update Vehicle' : 'Create Vehicle')}
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default VehicleForm;
